import { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ArrowRight, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const PROJECTS = [
  {
    id: "smartbtp",
    title: "Smart BTP — Devis Hiérarchique Odoo 19",
    desc: "Module Odoo 19 (sale_management) pour devis BTP : numérotation hiérarchique automatique (1 / 1.1 / 1.1.1), sous-totaux par section, pliage/dépliage en live et impression PDF conforme. En production sur RL Construction.",
    category: "module",
    tech: ["Odoo 19", "Python", "OWL", "QWeb"],
    img: "/projects/btpv2_lines.png",
    imgType: "odoo",
    client: "RL Construction",
    status: "En production",
    accent: "#2563eb",
  },
  {
    id: "aowatch",
    title: "AO Watch — Veille Appels d'Offres",
    desc: "Plateforme de veille automatisée des marchés publics DOM : scoring IA de pertinence, alertes email personnalisées par secteur, suivi des candidatures et statistiques par territoire (Réunion / Mayotte / Métropole).",
    category: "plateforme",
    tech: ["Python", "IA", "Dashboard", "Alertes"],
    img: "/projects/aowatch.png",
    imgType: "mockup",
    client: "Groupe RLC",
    status: "En production",
    accent: "#d97706",
  },
  {
    id: "gymtech",
    title: "GymTech — Gestion SAV Salles de Sport",
    desc: "Application de suivi des interventions techniques multi-sites : planning techniciens, gestion des urgences, coûts par salle, état des machines et rapport mensuel automatique avec export comptable.",
    category: "app",
    tech: ["React", "API REST", "Multi-sites"],
    img: "/projects/gymtech.png",
    imgType: "mockup",
    client: "Réseau salles Réunion",
    status: "Maquette validée",
    accent: "#16a34a",
  },
  {
    id: "idmat-adv",
    title: "IDMAT OI — Suivi ADV & Configurateur",
    desc: "Module Odoo 19 pour IDMAT OI (distribution BTP) : pipeline ADV en 9 étapes, configurateur d'engins (LiuGong, Rippa), checklist documents, suivi financement et tableau de bord commercial.",
    category: "module",
    tech: ["Odoo 19", "Python", "Configurateur", "ADV"],
    img: "/projects/idmat_configurateur.png",
    imgType: "mockup",
    client: "IDMAT OI",
    status: "En développement",
    accent: "#7c3aed",
  },
  {
    id: "emergent",
    title: "Emergent — Tâches & Messagerie",
    desc: "Application mobile collaborative iOS-style : gestion des tâches par liste et sprint, messagerie d'équipe en temps réel, statistiques de productivité hebdo et notifications push.",
    category: "app",
    tech: ["React Native", "iOS-style", "Temps réel"],
    img: "/projects/emergent.png",
    imgType: "mockup",
    client: "Groupe RLC interne",
    status: "Maquette",
    accent: "#0891b2",
  },
  {
    id: "smartbuilder",
    title: "SmartBuilder — Studio Apps par IA",
    desc: "Studio no-code de génération d'applications métier par IA générative : décris l'app en langage naturel, l'IA génère le code, prévisualise et déploie. Projets actifs : SmartRent, Menu Snack, Catalogue IDMAT.",
    category: "plateforme",
    tech: ["IA générative", "Node.js", "No-code", "SaaS"],
    img: "/projects/smartbuilder.png",
    imgType: "mockup",
    client: "Marché SaaS",
    status: "MVP actif",
    accent: "#0d9488",
  },
  {
    id: "previsionnel",
    title: "IDMAT Prévisionnel IA",
    desc: "Simulateur financier interactif guidé par IA : saisie en 5 étapes (société, chiffre d'affaires, charges, financement, résultats), conseiller IA intégré et export du bilan prévisionnel.",
    category: "app",
    tech: ["IA", "Simulateur", "Finance"],
    img: "/projects/previsionnel_ia.png",
    imgType: "mockup",
    client: "IDMAT OI",
    status: "Maquette",
    accent: "#e11d48",
  },
  {
    id: "leu-snack",
    title: "Leu Snack Des Châteaux — POS Odoo 19",
    desc: "Point de vente Odoo 19 pour restaurant à La Réunion : configuration magasin, menu du jour digital, gestion des commandes en salle et intégration stock temps réel.",
    category: "module",
    tech: ["Odoo 19 POS", "QWeb", "Restaurant"],
    img: "/projects/snack_pos.png",
    imgType: "odoo",
    client: "Leu Snack Des Châteaux",
    status: "Ouverture 2026",
    accent: "#dc2626",
  },
  {
    id: "dashboard-dirigeant",
    title: "Tableau de Bord Dirigeant",
    desc: "Dashboard exécutif dark-mode pour dirigeants de PME : KPIs du jour, agenda prioritaire, chiffre d'affaires, alertes critiques et routine de contrôle quotidienne assistée par IA.",
    category: "app",
    tech: ["Dashboard", "IA", "PME"],
    img: "/projects/dashboard_dirigeant.png",
    imgType: "mockup",
    client: "Groupe RLC",
    status: "Maquette",
    accent: "#c9a84c",
  },
];

const CATS = [
  { v: "all",       l: "Tous" },
  { v: "module",    l: "Modules Odoo" },
  { v: "app",       l: "Applications" },
  { v: "plateforme",l: "Plateformes" },
];

const STATUS_CLS = {
  "En production":   "bg-green-100 text-green-700",
  "En développement":"bg-blue-100 text-blue-700",
  "MVP actif":       "bg-teal-100 text-teal-700",
  "Maquette validée":"bg-amber-100 text-amber-700",
  "Maquette":        "bg-slate-100 text-slate-600",
  "Ouverture 2026":  "bg-red-100 text-red-700",
};

const IMG_LABEL = {
  odoo:   { txt: "Odoo 19 réel", cls: "bg-indigo-600 text-white" },
  mockup: { txt: "Maquette",     cls: "bg-slate-700/70 text-white backdrop-blur-sm" },
};

function ProjectCard({ p, index }) {
  const lbl = IMG_LABEL[p.imgType];
  return (
    <div
      className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-indigo-100 hover:shadow-xl transition-all duration-300"
      data-testid={`portfolio-item-${index}`}
    >
      <div className="relative overflow-hidden" style={{ height: 210 }}>
        <img src={p.img} alt={p.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${lbl.cls}`}>{lbl.txt}</span>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_CLS[p.status] || "bg-slate-100 text-slate-600"}`}>
            {p.status}
          </span>
        </div>
        <h3 className="text-base font-bold text-slate-900 mb-2 leading-snug">{p.title}</h3>
        <p className="text-sm text-slate-500 mb-4 leading-relaxed line-clamp-3">{p.desc}</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {p.tech.map((t) => (
            <Badge key={t} variant="outline" className="text-xs border-slate-200 text-slate-500">{t}</Badge>
          ))}
        </div>
        <div className="pt-4 border-t border-slate-100 text-sm text-slate-500">
          <span className="font-medium text-slate-700">{p.client}</span>
        </div>
      </div>
    </div>
  );
}

export default function PortfolioPage() {
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div className="min-h-screen" data-testid="portfolio-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white" />
        <div className="nova-container relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-indigo-600 text-xs font-bold tracking-widest uppercase mb-4">Portfolio</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6" data-testid="portfolio-title">
              Nos <span className="nova-gradient-text">réalisations</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Modules Odoo 19 en production, applications métier et plateformes IA — des projets concrets
              déployés à La Réunion.
            </p>
            <div className="flex gap-8 flex-wrap">
              {[["9+","Projets"], ["2","Écrans Odoo réels"], ["8+","Apps / Modules"]].map(([n, l]) => (
                <div key={l}>
                  <div className="text-3xl font-bold text-indigo-600">{n}</div>
                  <div className="text-sm text-slate-500">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filtres */}
      <section className="py-6 bg-white border-b border-slate-100 sticky top-20 z-30">
        <div className="nova-container">
          <div className="flex items-center gap-4 flex-wrap">
            <Filter className="w-5 h-5 text-slate-400 shrink-0" />
            <div className="flex gap-2 flex-wrap">
              {CATS.map((c) => (
                <Button key={c.v}
                  variant={filter === c.v ? "default" : "outline"}
                  className={filter === c.v ? "bg-indigo-600 text-white" : "border-slate-200 text-slate-600"}
                  onClick={() => setFilter(c.v)}
                  data-testid={`filter-${c.v}`}>
                  {c.l}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Grille */}
      <section className="nova-section bg-slate-50">
        <div className="nova-container">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((p, i) => <ProjectCard key={p.id} p={p} index={i} />)}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500 mb-4">Aucun projet dans cette catégorie.</p>
              <Button variant="outline" onClick={() => setFilter("all")}>Voir tout</Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="nova-section bg-white">
        <div className="nova-container">
          <div className="bg-slate-900 rounded-3xl p-12 md:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Votre projet sera notre prochaine réalisation
            </h2>
            <p className="text-slate-400 mb-10 max-w-2xl mx-auto">
              Modules Odoo sur mesure, applications métier ou plateformes IA — parlons-en.
            </p>
            <Link to="/contact">
              <Button className="nova-btn-primary bg-indigo-600 hover:bg-indigo-700 mr-4">
                Nous contacter <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/onboarding">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                Cadrer mon projet
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
